<template>
    <v-dialog :value="value" @input="$emit('input', $event)" :fullscreen="effectiveFullscreen" transition="dialog-bottom-transition" scrollable max-width="900px">
        <v-card>
            <v-toolbar dark :color="$store.state.theme.primary">
                <v-btn icon dark @click="$emit('input', false)">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-toolbar-title>Dein KI Assistent</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn v-if="!$vuetify.breakpoint.xsOnly" icon dark @click="toggleFullScreen">
                    <v-icon>{{ isFullScreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pa-0 d-flex flex-column" :style="{ height: cardTextHeight, overflow: 'hidden', position: 'relative' }">
                <!-- Terms Dialog -->
                <v-dialog v-model="dialog_google_terms" persistent max-width="600px">
                    <v-card>
                        <v-toolbar dark :color="$store.state.theme.primary">
                            <v-toolbar-title>Nutzungsbedingungen & Datenschutz</v-toolbar-title>
                        </v-toolbar>
                        <v-card-text class="text-body-1 mt-5 text-justify">
                            <p>
                                Ich bin Sigi, dein Freu(n)d und persönlicher KI-Assistent von Google.
                                Damit ich dir bei deinen Fragen helfen kann, sende ich deine Anfragen an Google.<br/><br/>
                                <b>Datenschutz ist mir aber sehr wichtig!</b><br/>
                                Ich speichere keine Daten von dir und unsere Gespräche werden nicht verwendet, um mich weiter zu verbessern.<br/><br/>

                                <b>Denk bitte daran:</b><br/>
                                Teile aber trotzdem keine sensiblen Daten wie Namen, Adressen, E-Mails oder ähnliches mit mir.
                                
                                Google legt bei ihrer KI großen Wert auf Datenschutz und Sicherheit, deshalb werden die Daten von Google nur so lange gespeichert bis meine KI-Antworten erstellt wurden.
                                Hier findest du weitere Informationen zu den <a href="https://cloud.google.com/vertex-ai/generative-ai/docs/data-governance?hl=de" target="_blank">Datenschutzbestimmungen von Google</a>.
                            </p>

                            <p class="mt-5">
                                Solltest du noch weitere Fragen haben, kannst du dich gerne an uns wenden.
                            </p>
                        </v-card-text>
                        <v-card-actions class="d-flex justify-center pb-5 mt-0 pt-0">
                            <v-btn text @click="rejectTerms" :color="$store.state.theme.red" class="mr-2">
                                Ablehnen
                            </v-btn>
                            <v-btn @click="acceptTerms" :color="$store.state.theme.green" dark class="ml-2">
                                Akzeptieren
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- Main Assistant Content Area -->
                <div :class="[
                    'assistent-content-area',
                    'd-flex',
                    'flex-column',
                    'flex-grow-1',
                    { 'justify-center': messages.length === 1 && !messages[0].isUser }
                ]" style="position: relative; overflow: hidden;">
                     <!-- Empty spacer div for welcome screen REMOVED -->
                    <!-- <div v-if="messages.length === 1 && !messages[0].isUser" class="flex-grow-0"></div> -->

                    <!-- Message Container -->
                    <div ref="scrollableContainer" :class="[
                        'message-container', 
                        { 'centered-welcome-container': messages.length === 1 && !messages[0].isUser }
                    ]">
                        <v-row v-for="(message, index) in messages" :key="index" :class="[
                            message.isUser ? 'sent' : 'received', 
                            { 'welcome-message': messages.length === 1 && !message.isUser }
                        ]" class="d-flex align-start message-item mx-0 my-0">
                            <v-col cols="auto" v-if="!message.isUser" class="pa-0">
                                <v-progress-circular v-if="message.loading" indeterminate color="green" size="24"></v-progress-circular>
                                <svg v-else-if="messages.length > 1 && !message.isUser" version="1.0" xmlns="http://www.w3.org/2000/svg"
                                    width="28pt" height="28pt" viewBox="0 0 1024.000000 1024.000000"
                                    preserveAspectRatio="xMidYMid meet">
                                    <g transform="translate(0.000000,1024.000000) scale(0.100000,-0.100000)"
                                    fill="#000000" stroke="none">
                                    <path d="M4875 9140 c-465 -40 -1012 -222 -1375 -458 -311 -202 -576 -461
                                    -774 -757 -54 -80 -160 -287 -201 -390 -15 -38 -41 -115 -57 -170 -54 -186
                                    -58 -241 -58 -822 0 -482 -2 -533 -16 -533 -9 0 -57 -14 -108 -31 -221 -73
                                    -395 -225 -496 -434 -54 -110 -79 -216 -86 -360 -12 -246 48 -452 184 -633
                                    115 -154 260 -250 472 -313 l45 -14 6 -405 c7 -443 9 -468 74 -706 156 -571
                                    466 -1031 931 -1380 386 -290 773 -455 1279 -545 150 -27 519 -37 677 -20 128
                                    15 371 63 492 98 334 95 685 274 941 480 455 364 752 809 898 1343 67 243 70
                                    275 76 724 l6 408 89 29 c299 98 502 313 589 624 24 88 34 319 18 422 -27 175
                                    -98 324 -216 451 -113 122 -262 213 -413 252 l-67 17 -6 574 -5 574 -27 110
                                    c-90 369 -264 681 -562 1009 -329 361 -897 673 -1444 792 -259 57 -632 84
                                    -866 64z m483 -371 c311 -33 592 -113 877 -248 233 -111 409 -230 608 -413
                                    286 -261 515 -658 556 -960 5 -41 10 -362 10 -736 l1 -663 -46 3 -47 3 -17 65
                                    c-66 253 -261 515 -483 649 -172 104 -340 152 -525 152 -294 -1 -525 -98 -737
                                    -308 -130 -130 -217 -276 -269 -454 l-24 -84 -169 -3 -170 -2 -13 57 c-17 78
                                    -77 215 -135 310 -67 108 -234 273 -340 335 -106 61 -216 104 -340 129 -83 18
                                    -123 20 -235 16 -160 -6 -239 -26 -385 -96 -186 -90 -327 -212 -440 -381 -82
                                    -122 -131 -235 -159 -362 -6 -26 -10 -28 -51 -28 l-45 0 -1 178 c-5 535 1
                                    1152 12 1221 15 102 70 262 131 385 281 562 857 998 1548 1171 257 64 636 92
                                    888 64z m-1242 -2541 c104 -34 179 -83 271 -174 67 -67 89 -98 127 -176 54
                                    -110 76 -201 76 -316 0 -217 -75 -388 -237 -543 -131 -125 -274 -182 -453
                                    -182 -267 0 -489 144 -615 398 -140 284 -82 602 149 830 91 89 187 146 296
                                    177 94 26 287 20 386 -14z m2344 11 c122 -34 204 -83 306 -184 79 -78 98 -103
                                    137 -185 139 -288 87 -602 -137 -826 -157 -157 -323 -219 -540 -202 -172 13
                                    -313 82 -431 210 -76 81 -119 154 -159 265 -29 83 -31 96 -31 233 0 133 2 151
                                    28 225 84 238 262 406 499 469 82 22 239 20 328 -5z m1396 -640 c118 -56 208
                                    -164 245 -294 21 -74 18 -283 -5 -360 -43 -145 -159 -275 -288 -324 l-28 -11
                                    0 510 c0 281 2 510 5 510 3 0 35 -14 71 -31z m-5450 -479 c0 -297 -4 -500 -9
                                    -500 -5 0 -36 14 -69 32 -186 99 -288 340 -246 584 21 124 55 193 136 272 55
                                    55 145 112 177 112 7 0 10 -174 11 -500z m2849 263 c34 -161 77 -274 144 -371
                                    l42 -63 -3 -332 -3 -332 -33 -68 c-42 -84 -121 -149 -206 -168 -137 -30 -284
                                    9 -366 97 -84 92 -85 95 -85 469 l0 330 51 80 c60 95 96 181 119 290 10 44 20
                                    83 22 88 2 4 74 7 158 7 l155 0 5 -27z m-2380 -45 c11 -77 60 -223 102 -298
                                    121 -221 336 -413 551 -494 139 -52 173 -58 327 -63 127 -4 165 -2 243 16 75
                                    16 184 54 280 98 9 4 12 -33 12 -165 0 -198 8 -248 60 -357 136 -290 466 -448
                                    795 -380 209 43 357 148 468 333 69 112 81 165 85 375 2 103 7 187 12 187 4 0
                                    37 -13 73 -30 139 -63 203 -75 407 -75 173 1 191 2 275 28 370 114 646 401
                                    733 764 l27 113 43 0 42 0 0 -414 0 -413 -67 4 c-59 4 -72 2 -102 -19 -70 -48
                                    -75 -62 -82 -241 -8 -196 -31 -282 -109 -402 -94 -144 -196 -229 -330 -277
                                    -59 -21 -82 -23 -260 -23 l-195 0 -93 34 c-141 50 -213 35 -253 -52 -52 -115
                                    8 -217 151 -258 61 -17 64 -22 45 -60 -57 -112 -170 -212 -281 -249 -89 -30
                                    -259 -25 -347 9 -89 35 -203 116 -300 215 l-88 89 -79 -85 c-99 -106 -217
                                    -188 -323 -223 -63 -21 -98 -26 -175 -26 -202 0 -334 78 -441 259 l-31 53 23
                                    8 c12 5 44 17 70 26 89 31 137 90 137 165 0 50 -19 89 -59 123 -51 43 -91 43
                                    -221 1 -113 -36 -118 -37 -272 -36 -175 0 -225 10 -341 69 -160 80 -315 282
                                    -352 461 -9 39 -15 124 -15 203 0 124 -2 139 -22 169 -34 49 -83 72 -143 67
                                    -27 -2 -57 -7 -65 -11 -15 -6 -16 57 -11 702 l1 132 44 0 44 0 7 -52z m2
                                    -1689 c44 -63 147 -167 215 -218 178 -130 350 -191 537 -191 l78 0 28 -66 c88
                                    -206 263 -384 467 -472 104 -45 192 -62 324 -62 192 0 348 52 502 166 l72 55
                                    66 -51 c78 -60 154 -100 261 -137 72 -25 90 -27 238 -28 191 0 243 11 394 86
                                    171 86 324 249 390 418 14 36 30 72 34 79 6 8 38 12 101 12 310 0 624 206 796
                                    523 l40 72 0 -142 c0 -247 -32 -421 -125 -679 -141 -386 -361 -691 -700 -966
                                    -417 -339 -947 -528 -1479 -528 -273 0 -553 47 -804 135 -213 74 -516 241
                                    -697 383 -181 143 -376 354 -500 542 -129 196 -208 368 -272 595 -49 175 -62
                                    261 -68 472 l-6 188 38 -70 c21 -38 53 -91 70 -116z"/>
                                    <path d="M4782 8192 c-34 -10 -90 -59 -108 -94 -11 -22 -15 -49 -12 -90 4 -47
                                    10 -64 34 -89 41 -43 76 -59 129 -59 56 0 92 18 133 64 105 120 -21 312 -176
                                    268z"/>
                                    <path d="M5960 8138 c-52 -15 -90 -46 -111 -92 -36 -79 -20 -157 44 -213 82
                                    -72 207 -48 268 51 48 78 4 206 -84 246 -47 22 -66 23 -117 8z"/>
                                    <path d="M4102 7673 c-18 -9 -44 -35 -59 -60 -35 -60 -30 -139 12 -183 84 -88
                                    203 -71 255 36 64 130 -78 271 -208 207z"/>
                                    <path d="M4575 6981 c-136 -61 -136 -251 -1 -312 38 -17 75 -19 542 -19 l501
                                    0 33 23 c44 29 69 65 80 113 17 75 -25 163 -93 194 -40 18 -70 20 -532 20
                                    -454 0 -493 -2 -530 -19z"/>
                                    <path d="M3778 5835 c-138 -72 -180 -269 -81 -384 73 -86 172 -117 274 -88
                                    221 65 243 376 34 473 -70 33 -162 32 -227 -1z"/>
                                    <path d="M6173 5830 c-201 -115 -173 -403 46 -467 162 -47 323 70 325 236 0
                                    76 -19 126 -68 179 -54 60 -104 82 -186 82 -54 0 -74 -5 -117 -30z"/>
                                    </g>
                                </svg>
                            </v-col>
                            <v-col class="message-content" :class="{ 'welcome-content': messages.length === 1 && !message.isUser }">
                                <!-- Loading indicator for assistant -->
                                <div class="message-placeholder" v-if="!message.isUser && index === messages.length - 1 && message.text === '' && message.loading">
                                    <span>Ich überlege...</span>
                                </div>
                                <!-- Message content (bubble for user, plain for assistant) -->
                                <div 
                                    :class="{ 
                                        'message-bubble': message.isUser, 
                                        'assistant-text': !message.isUser,
                                        'welcome-text': messages.length === 1 && !message.isUser 
                                    }" 
                                    v-if="message.text !== ''" 
                                >
                                    <span v-html="renderMarkdown(message.text !== undefined ? message.text.substring(0, message.text.length - (message.newChunk ? message.newChunk.length : 0)) : '')"></span>
                                    <span class="new-chunk" v-if="message.newChunk" v-html="renderMarkdown(message.newChunk)"></span>
                                </div>
                            </v-col>
                            <v-col cols="auto" v-if="message.isUser">
                                <v-icon>mdi-account-circle</v-icon>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Input Container MOVED INSIDE -->
                    <div class="d-flex justify-center flex-shrink-0">
                        <v-card ref="inputCardRef" class="py-5 px-7 input-card" :class="{ 'welcome-input': messages.length === 1 && !messages[0].isUser }">
                            <v-row class="mt-0 mb-2">
                                <textarea 
                                    v-model="userInput" 
                                    placeholder="Stelle irgendeine Frage" 
                                    @keydown.enter="handleEnter"
                                    :disabled="isStreaming"
                                    class="dark-text-input"
                                    rows="1"
                                    ref="textInput"
                                    :autofocus="value"
                                    @input="adjustTextareaHeight"
                                    :style="{ overflowY: exceededMaxHeight ? 'auto' : 'hidden' }"
                                ></textarea>
                            </v-row>

                            <v-row>
                                <v-chip class="mr-2 mb-2" @click="deleteHistory">
                                        <v-icon :left="$vuetify.breakpoint.smAndUp" small>mdi-delete</v-icon>
                                        {{ $vuetify.breakpoint.smAndUp ? 'Chat zurücksetzen' : '' }}
                                    </v-chip>
                                <v-chip class="mb-2" @click="introduce">
                                        <v-icon :left="$vuetify.breakpoint.smAndUp" small>mdi-comment-question</v-icon>
                                        {{ $vuetify.breakpoint.smAndUp ? 'Wer bist du und was kannst du alles?' : '' }}
                                    </v-chip>
                                    <v-spacer></v-spacer>
                                    <v-btn icon outlined
                                        :color="$store.state.theme.green"
                                        @click="sendMessage" 
                                        :disabled="isStreaming || !userInput.trim()">
                                        <v-icon>mdi-send</v-icon>
                                    </v-btn>
                            </v-row>
                        </v-card>
                    </div>
                </div>

                <!-- Scroll to bottom button ADDED HERE, directly inside v-card-text -->
                <v-btn
                    v-if="!isScrolledToBottom"
                    elevation="1"
                    fab
                    color="white"
                    x-small
                    @click="scrollToBottom"
                    class="scroll-bottom-button"
                    :style="{ bottom: scrollButtonBottomOffset + 'px' }"
                >
                    <v-icon>mdi-arrow-down</v-icon>
                </v-btn>

                <p v-if="messages.length > 1" class="text-center text-caption py-0 mt-0 px-0 flex-shrink-0">Ich kann Fehler machen. Überprüfe {{ $vuetify.breakpoint.smAndUp ? 'daher' : '' }} wichtige Informationen.</p>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import MarkdownIt from "markdown-it";
import connector from "../helpers/supabase-connector.js";

export default {
    props: {
        value: Boolean, // Used for v-model
        session: Object
    },
    data() {
        return {
            dialog_google_terms: false,
            contents: [],
            userInput: "",
            messages: [{ text: "Hallo! Wie kann ich dir helfen?", isUser: false }],
            isStreaming: false,
            newChunk: "",
            exceededMaxHeight: false,
            isScrolledToBottom: true, // Track scroll state
            scrollButtonBottomOffset: 100, // Default offset, will be calculated
            isFullScreen: false, // Add state for fullscreen toggle
        };
    },

    computed: {
        exceededLines() {
            return this.userInput.split("\n").length > 6;
        },
        // Calculate height dynamically based on fullscreen state using dvh
        cardTextHeight() {
            // Use effectiveFullscreen for height calculation, using dvh for mobile compatibility
            return this.effectiveFullscreen ? 'calc(100dvh - 64px)' : 'calc(85dvh - 64px)';
        },
        // Determine effective fullscreen state based on internal state and breakpoint
        effectiveFullscreen() {
            return this.isFullScreen || this.$vuetify.breakpoint.xsOnly;
        }
        // totalHeight is no longer needed as the card-text handles the height
    },
    watch: {
        // Watch for the dialog opening to initialize
        value(newValue) {
            if (newValue) {
                this.initializeAssistant();
            }
        }
    },

    mounted() {
        // Initialization logic moved to initializeAssistant, called when dialog opens
        // Add resize listener for dynamic button positioning
        window.addEventListener('resize', this.updateScrollButtonPosition);
    },

    methods: {
        initializeAssistant() {
            // Load history
            const storedContents = localStorage.getItem("assistent-contents");
            if (storedContents) {
                this.contents = JSON.parse(storedContents);
                // convert role: 'MODEL' -> role: 'model' as this is a breaking change (switch from vertex to GoogleApi)
                this.contents = this.contents.map((content) => {
                    content.role = content.role.toLowerCase();
                    return content;
                });

                this.messages = [{ text: "Hallo! Wie kann ich dir helfen?", isUser: false }].concat(this.contents.map((content) => {
                    return { text: content.parts[0].text, isUser: content.role === "user"};
                }));
            } else {
                 // Reset if no stored contents
                 this.messages = [{ text: "Hallo! Wie kann ich dir helfen?", isUser: false }];
                 this.contents = [];
            }
            
            // Focus the text input when component is mounted and visible
            this.$nextTick(() => {
                if (this.$refs.textInput) {
                    this.$refs.textInput.focus();
                    this.adjustTextareaHeight(); // Adjust height on open
                }
                this.scrollToBottom(); // Scroll to bottom on open
                this.addScrollListener(); // Add scroll listener only when dialog is open and container exists
                this.updateScrollButtonPosition(); // Calculate initial button position
            });
        },

        addScrollListener() {
            this.$nextTick(() => {
                const container = this.$refs.scrollableContainer;
                if (container) {
                    container.removeEventListener('scroll', this.handleScroll); // Remove previous listener if exists
                    container.addEventListener('scroll', this.handleScroll);
                    this.handleScroll(); // Check initial scroll position
                }
            });
        },

        removeScrollListener() {
            const container = this.$refs.scrollableContainer;
            if (container) {
                container.removeEventListener('scroll', this.handleScroll);
            }
        },

        acceptTerms() {
            localStorage.setItem('agreed_google_terms_assistent', true);
            this.dialog_google_terms = false;

            if (this.session) { 
                connector.logError(this, {
                    uid: this.session.user.id,
                    message: this.session.user.email +  ' accepted the Google terms assisstent',
                });
            }
            // Attempt to send message again if terms were blocking
            if (this.pendingUserInput) {
                this.userInput = this.pendingUserInput;
                this.pendingUserInput = null;
                this.sendMessage();
            }
        },

        rejectTerms() {
            if (this.session) { 
                connector.logError(this, {
                    uid: this.session.user.id,
                    message: this.session.user.email +  ' REJECTED the Google terms assisstent',
                });
            }
            this.dialog_google_terms = false;
            this.pendingUserInput = null; // Clear pending input if rejected
            this.$emit('input', false); // Close the dialog if terms rejected
        },

        deleteHistory() {
            this.messages = [{ text: "Hallo! Wie kann ich dir helfen?", isUser: false }];
            this.contents = [];
            localStorage.removeItem("assistent-contents");
            this.$nextTick(() => {
                this.updateScrollButtonPosition(); // Recalculate button position after reset
            });
        },

        introduce() {
            this.userInput = "Wer bist du und was kannst du alles?";
            this.sendMessage();
        },

        handleEnter(event) {
            if (!event.shiftKey) {
                this.sendMessage();
                event.preventDefault(); // prevent the default action (new line)
            } else {
                 // Allow shift+enter for newline and readjust height
                 this.$nextTick(() => this.adjustTextareaHeight());
            }
        },

        renderMarkdown(text) {
            const md = new MarkdownIt();
            return md.render(text);
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.scrollableContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
                // Ensure state is updated immediately after setting scrollTop
                this.isScrolledToBottom = true; 
            });
        },

        async sendMessage() {

            if (this.userInput.trim() === "") return;

            // check if user has accepted the terms
            const agreed_google_terms_assistent = localStorage.getItem('agreed_google_terms_assistent');
            if (!agreed_google_terms_assistent) {
                this.pendingUserInput = this.userInput; // Store input temporarily
                this.dialog_google_terms = true;
                return;
            }

            let stored_input = this.userInput.trim();

            this.contents.push({
                role: "user",
                parts: [
                    {
                        text: stored_input,
                    },
                ],
            });
            // Add the user message to the chat
            // Ensure the welcome message isn't displayed if it was the only message
            if (this.messages.length === 1 && !this.messages[0].isUser) {
                 this.messages = []; // Clear welcome message if first user message is sent
            }
            this.messages.push({ text: stored_input, isUser: true, loading: false });

            this.scrollToBottom(); 

            // Clear the input
            this.userInput = "";
            
            // Reset textarea height
            this.$nextTick(() => {
                if (this.$refs.textInput) {
                    this.$refs.textInput.style.height = 'auto'; // Reset before calculating new height
                    this.adjustTextareaHeight(); // Adjust to single line
                }
            });

            this.messages.push({
                text: "",
                isUser: false,
                loading: true,
            });
            this.$nextTick(() => this.scrollToBottom()); // Scroll down for loading indicator

            try {
                this.isStreaming = true;
                var myHeaders = new Headers();
                myHeaders.append("Authorization", "Bearer " + this.session.access_token);
                myHeaders.append("Accept", "*/*");
                myHeaders.append("x-client-info", "supabase-js/2.8.0");

                var requestOptions = {
                    method: "POST",
                    headers: myHeaders,
                    redirect: "follow",
                    body: JSON.stringify({
                        contents: this.contents,
                        type: "assistentStream3",
                    }),
                };

                let response = await fetch("https://qgsfsflpvymafizvkpca.functions.supabase.co/get-text-response/", requestOptions);
                
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let result = "";
                let displayedText = "";
                let buffer = ""; // Buffer to accumulate chunks

                // Ensure the loading indicator is replaced immediately
                if (this.messages.length > 0 && this.messages[this.messages.length - 1].loading) {
                    this.messages[this.messages.length - 1].loading = false;
                }


                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    
                    buffer += decoder.decode(value, { stream: true }); // Append chunk to buffer

                    let newlineIndex;
                    // Process all complete lines (JSON objects) in the buffer
                    // Reverted parsing logic to be simpler like original
                    while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
                        const jsonString = buffer.substring(0, newlineIndex).trim(); // Extract line
                        buffer = buffer.substring(newlineIndex + 1); // Remove line from buffer

                        if (jsonString) { // Ensure it's not an empty line
                            try {
                                const data = JSON.parse(jsonString);
                                if (data.text) {
                                    // Store previous result before adding the new chunk
                                    const prevResult = result;
                                    // Add new text to the result
                                    result += data.text;
                                    
                                    if (this.messages.length > 0 && !this.messages[this.messages.length - 1].isUser) {
                                        // Clear previous new chunk by setting the full text without highlighting
                                        const currentMessage = this.messages[this.messages.length - 1];
                                        
                                        // Set the full text, but also store the new chunk separately
                                        currentMessage.text = result;
                                        currentMessage.newChunk = data.text;
                                        
                                        // Check if scrolled to bottom before updating, only auto-scroll if already at bottom
                                        const container = this.$refs.scrollableContainer;
                                        const shouldScroll = container && (container.scrollHeight - container.scrollTop - container.clientHeight < 30); // Tolerance for scrolling
                                        if (shouldScroll) {
                                            this.scrollToBottom();
                                        } else {
                                            this.isScrolledToBottom = false; // If not scrolling, show the button
                                        }
                                    }
                                    displayedText = result;
                                }
                            } catch (error) {
                                console.error('Failed to parse JSON line:', jsonString, error);
                                // Simplified error handling - log and continue
                                connector.logError(this, {
                                    uid: this.session.user.id,
                                    message: 'Failed to parse NDJSON line: ' + jsonString,
                                    error: error
                                });
                            }
                        }
                    }
                }

                // Process any remaining data in the buffer (might be the last chunk without newline)
                // Removed final buffer processing for simplicity, assuming stream ends with newline
                // if (buffer.trim()) { ... }


                // After streaming is complete, clear the highlight on the final message
                if (this.messages.length > 0 && !this.messages[this.messages.length - 1].isUser) {
                    this.messages[this.messages.length - 1].newChunk = "";
                    this.messages[this.messages.length - 1].text = result; // Ensure final text is set
                }


                // Add the complete message to contents after streaming is done
                if (result) { // Only add if we got a response
                    this.contents.push({
                        role: "model",
                        parts: [
                            {
                                text: result,
                            },
                        ],
                    });
                    localStorage.setItem("assistent-contents", JSON.stringify(this.contents));
                }

                // Ensure scroll state is correct after finishing
                this.$nextTick(() => this.handleScroll());


            } catch (error) {
                console.error("Assistant Error:", error);
                 if (this.messages[this.messages.length - 1].loading) {
                     this.messages.pop(); // Remove loading message
                 }
                connector.logError(this, {
                    uid: this.session?.user?.id || 'unknown',
                    message: 'Assistant sendMessage failed: ' + error.message,
                    error: error.toString(),
                });
                this.messages.push({
                    text: "Entschuldigung, ich konnte deine Frage nicht beantworten. Bitte versuche es erneut. Eventuell hilft es den Chat zurückzusetzen.",
                    isUser: false,
                    loading: false,
                });
                this.userInput = stored_input; // Restore user input on failure
            } finally {
                this.isStreaming = false;
                // Refocus the textarea after streaming completes or fails
                this.$nextTick(() => {
                    if (this.$refs.textInput) {
                        this.$refs.textInput.focus();
                    }
                     // Update scroll button position in case content changed significantly
                    this.updateScrollButtonPosition();
                    // Ensure final scroll check
                     this.handleScroll();
                });
            }
        },

        adjustTextareaHeight() {
            this.$nextTick(() => {
                const textarea = this.$refs.textInput;
                if (!textarea) return;

                textarea.style.height = 'auto'; // Temporarily shrink to get the real scrollHeight
                const scrollHeight = textarea.scrollHeight;
                
                // Get container width to adjust behavior based on window size
                const containerWidth = textarea.parentElement?.offsetWidth || 400; // Fallback width
                
                // Adjust max height based on container width (example values)
                const maxHeight = containerWidth < 600 ? 120 : 150; // Smaller max height on narrow views
                
                if (scrollHeight <= maxHeight) {
                    textarea.style.height = scrollHeight + 'px';
                    this.exceededMaxHeight = false;
                    textarea.style.overflowY = 'hidden'; // Hide scrollbar if not needed
                } else {
                    textarea.style.height = maxHeight + 'px';
                    this.exceededMaxHeight = true;
                     textarea.style.overflowY = 'auto'; // Show scrollbar if needed
                }
                // Update button position after height adjustment
                 this.updateScrollButtonPosition();
            });
        },

        handleScroll() {
            const container = this.$refs.scrollableContainer;
            if (container) {
                // Use a tolerance (e.g., 10 pixels) for comparison to handle variations
                const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 10;
                this.isScrolledToBottom = isAtBottom;
            } else {
                 this.isScrolledToBottom = true; // Assume bottom if container not ready
            }
        },

        updateScrollButtonPosition() {
             this.$nextTick(() => {
                 // Simplified logic closer to original Assistent.vue context
                const inputCardElement = this.$refs.inputCardRef?.$el;
                // const bottomTextElement = document.querySelector('.v-card-text > p.text-caption');

                let inputCardHeight = 0;
                if (inputCardElement) {
                    inputCardHeight = inputCardElement.offsetHeight;
                }
                
                // Use fixed margin/gap values based on original context/styling
                const inputCardMarginBottom = 25; // As per original Assistent.vue styling context for spacing
                const disclaimerHeight = 20; // Approximate height of the disclaimer text line
                const gap = 5; // Original gap

                let totalInputAreaHeight = inputCardHeight + inputCardMarginBottom;
                
                // Add disclaimer height only if messages exist (it appears when messages.length > 1)
                if (this.messages.length > 1) {
                    totalInputAreaHeight += disclaimerHeight;
                }

                this.scrollButtonBottomOffset = totalInputAreaHeight + gap;
             });
        },

        // Method to toggle fullscreen state
        toggleFullScreen() {
            this.isFullScreen = !this.isFullScreen;
            // Recalculate positions after fullscreen change
            this.$nextTick(() => {
                this.updateScrollButtonPosition();
                this.adjustTextareaHeight();
                this.handleScroll(); // Update scroll state
            });
        }
    },

    beforeDestroy() {
        // Remove listeners when component is destroyed
        this.removeScrollListener();
        window.removeEventListener('resize', this.updateScrollButtonPosition);
    },
};
</script>

<style scoped>
/* Inherit styles from Assistent.vue where applicable, add dialog specific adjustments */

.assistent-content-area {
     height: 100%; /* Fill the v-card-text space */
    padding: 0;
    /* height: 100%; */ /* Remove height */
    /* flex-grow: 1; */ /* Remove grow */
    flex-grow: 0; /* Don't grow */
}


.new-chunk {
    color: #9e9e9e;
    /* Removed transition for performance in dialog */
    /* transition: color 1.5s ease; */
}

.message-container {
    flex-grow: 1;
    overflow-y: auto;
    background-color: transparent;
    /* max-height is handled by flexbox now */
    padding-top: 15px; /* Add some padding at the top */
    padding-left: 15px;
    padding-right: 15px;
    padding-bottom: 10px; /* Space before input */
}

.centered-welcome-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0;
    padding: 0;
    /* height: 100%; */ /* Remove height */
    /* flex-grow: 1; */ /* Remove grow */
    flex-grow: 0; /* Don't grow */
}


.message-bubble ::v-deep p,
.assistant-text ::v-deep p {
  margin-bottom: 9px !important;
}

.message-item {
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
    user-select: text;
    margin-bottom: 16px;
    max-width: 100%; /* Ensure messages dont overflow horizontally */
}

.message-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0 8px; /* Reduced padding */
    max-width: calc(100% - 40px); /* Prevent content overlap with icons */
}

.sent .message-content {
    align-items: flex-end;
}

.message-bubble {
    user-select: text;
    padding: 10px 14px;
    border-radius: 18px;
    display: inline-block;
    word-wrap: break-word;
    max-width: 70%; /* Keep max width relative */
    line-height: 1.4;
    box-shadow: none;
    font-size: 1rem;
    padding-top: 18px; /* Keep original padding */
    padding-bottom: 9px;
}

.assistant-text {
    padding: 6px 8px 10px 0;
    max-width: 90%; /* Keep max width relative */
    word-wrap: break-word;
    line-height: 1.5;
    color: #1c1c1e;
    font-size: 1rem;
    /* white-space: pre-wrap; */ /* Removed for better flow */
}

.message-placeholder {
    color: #6c757d;
    font-style: italic;
    padding: 8px;
}

.sent .message-bubble {
    background-color: #f0f0f0; /* Slightly different background for user maybe? */
    color: #1c1c1e;
    border-radius: 18px;
}

/* Input card styling - mostly reused */
.input-card {
    border-radius: 30px !important;
    max-width: 800px;
    width: calc(100% - 30px);
    margin-bottom: 5px; /* Reduced bottom margin inside dialog */
    margin-left: auto;
    margin-right: auto;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

/* Welcome specific styles reused */
.welcome-message {
    width: 100%;
    justify-content: center;
    margin-bottom: 0;
}

.welcome-content {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1; /* Ensure it takes space for centering */
}

.welcome-text {
    font-size: 1.5rem;
    font-weight: bold;
    text-align: center;
    max-width: 80%;
    padding: 10px;
}

.welcome-input {
    margin-top: 0; /* Adjust spacing for welcome screen */
    margin-bottom: 0;
}

/* Text Input Area */
.dark-text-input {
    width: 100%;
    background: transparent;
    border: none;
    color: #1c1c1e;
    font-size: 16px;
    padding: 8px;
    outline: none;
    resize: none;
    /* max-height defined dynamically */
    min-height: 24px;
    font-family: inherit;
    line-height: 1.5;
    overflow-y: hidden; /* Managed by adjustTextareaHeight */
    scrollbar-width: thin;
    scrollbar-color: #aaa #f0f0f0;
    box-sizing: border-box; 
}

.dark-text-input::-webkit-scrollbar {
    width: 6px;
}

.dark-text-input::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 3px;
}

.dark-text-input::-webkit-scrollbar-thumb {
    background-color: #aaa;
    border-radius: 3px;
}

.dark-text-input::placeholder {
    color: #aaa;
}

.v-col[cols="auto"] {
    flex-grow: 0;
    padding-right: 8px;
}

.scroll-bottom-button {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    /* bottom is calculated dynamically */
    z-index: 10; /* Ensure it's above messages */
}

/* Style adjustments for markdown content inside messages */
.message-bubble ::v-deep *, .assistant-text ::v-deep * {
    user-select: text; /* Allow selecting text within markdown */
    word-wrap: break-word; /* Ensure long words/code blocks wrap */
}

.message-bubble ::v-deep code, .assistant-text ::v-deep code {
  background-color: rgba(0,0,0,0.05);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: monospace;
}

.message-bubble ::v-deep pre, .assistant-text ::v-deep pre {
  background-color: rgba(0,0,0,0.05);
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto; /* Allow horizontal scrolling for code blocks */
  max-width: 100%;
}

.message-bubble ::v-deep pre code, .assistant-text ::v-deep pre code {
  background-color: transparent;
  padding: 0;
  white-space: pre; /* Preserve whitespace in code blocks */
}

/* Ensure links are visible */
.message-bubble ::v-deep a, .assistant-text ::v-deep a {
    color: #1976D2; /* Vuetify primary blue */
    text-decoration: underline;
}

</style> 